import * as React from "react";
import DatePicker, { CalendarContainer } from "react-datepicker";
import { Box, Flex, Icon, chakra } from "@chakra-ui/react";
import { getMonth, getYear } from "date-fns";
import { SiChevronLeft, SiChevronRight } from "@medstonetech/slate-icons";

import "react-datepicker/dist/react-datepicker.css";
import "../calendarInput/calendarInput.css";

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CkCalendarContainer = chakra(CalendarContainer);

type InlineCalendarProps = {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
};

function InlineCalendar({
  selected,
  onChange,
  minDate,
  maxDate,
}: InlineCalendarProps) {
  return (
    <DatePicker
      inline
      selected={selected}
      onChange={onChange}
      minDate={minDate}
      maxDate={maxDate}
      formatWeekDay={(nameOfDay) => nameOfDay.substring(0, 3).toUpperCase()}
      renderCustomHeader={({
        date,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
        decreaseYear,
        increaseYear,
        prevYearButtonDisabled,
        nextYearButtonDisabled,
      }) => (
        <Box mx="15px" mb="10px" mt="6px">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex alignItems="center">
              <chakra.button
                type="button"
                aria-label="Previous month"
                disabled={prevMonthButtonDisabled}
                onClick={decreaseMonth}
              >
                <Icon as={SiChevronLeft} fontSize="1rem" />
              </chakra.button>
              <chakra.span
                fontSize="20px"
                fontWeight="bold"
                minW="120px"
                textAlign="center"
              >
                {months[getMonth(date)]}
              </chakra.span>
              <chakra.button
                type="button"
                aria-label="Next month"
                disabled={nextMonthButtonDisabled}
                onClick={increaseMonth}
              >
                <Icon as={SiChevronRight} fontSize="1rem" />
              </chakra.button>
            </Flex>
            <Flex alignItems="center">
              <chakra.button
                type="button"
                aria-label="Previous year"
                disabled={prevYearButtonDisabled}
                onClick={decreaseYear}
              >
                <Icon as={SiChevronLeft} fontSize="1rem" />
              </chakra.button>
              <chakra.span
                fontSize="20px"
                fontWeight="bold"
                minW="60px"
                textAlign="center"
              >
                {getYear(date)}
              </chakra.span>
              <chakra.button
                type="button"
                aria-label="Next year"
                disabled={nextYearButtonDisabled}
                onClick={increaseYear}
              >
                <Icon as={SiChevronRight} fontSize="1rem" />
              </chakra.button>
            </Flex>
          </Flex>
        </Box>
      )}
      calendarContainer={({ children, ...calendarProps }) => (
        <CkCalendarContainer
          {...calendarProps}
          fontFamily="body"
          border="1px solid #D1D1D6"
          boxShadow="0px 4px 4px rgba(50, 50, 71, 0.08), 0px 4px 8px rgba(50, 50, 71, 0.06)"
          borderRadius="13px"
          fontSize="unset"
          padding="1"
        >
          {children}
        </CkCalendarContainer>
      )}
    />
  );
}

InlineCalendar.displayName = "InlineCalendar";

export { InlineCalendar };
export type { InlineCalendarProps };
